<template>
    <div class="photo-tile-container">
      <!-- Плитка галереи -->
      <div 
        class="photo-tile group cursor-pointer relative overflow-hidden rounded-xl bg-gray-100 shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-105"
        @click="openModal"
        :style="{ aspectRatio: '1/1' }"
      >
        <img 
          :src="photo.thumbnail || photo.url" 
          :alt="photo.alt || 'Gallery image'"
          class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />
        
        <!-- Оверлей при наведении -->
        <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
          <div class="opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform translate-y-2 group-hover:translate-y-0">
            <div class="bg-white bg-opacity-90 rounded-full p-3 backdrop-blur-sm">
              <svg class="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"/>
              </svg>
            </div>
          </div>
        </div>
  
        <!-- Индикатор загрузки -->
        <div v-if="imageLoading" class="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
  
      <!-- Модальное окно -->
      <Teleport to="body">
        <div 
          v-if="isModalOpen"
          class="modal-overlay fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-90 backdrop-blur-sm"
          @click="closeModal"
          @keydown.esc="closeModal"
          tabindex="0"
          ref="modalRef"
        >
          <!-- Контейнер модального окна -->
          <div 
            class="modal-content relative max-w-7xl max-h-full w-full h-full flex items-center justify-center"
            @click.stop
          >
            <!-- Кнопка закрытия -->
            <button 
              @click="closeModal"
              class="absolute top-4 right-4 z-10 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full p-2 transition-all duration-200 hover:scale-110"
              aria-label="Закрыть"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
  
            <!-- Изображение в полном размере -->
            <div class="relative max-w-full max-h-full flex items-center justify-center">
              <img 
                :src="photo.url" 
                :alt="photo.alt || 'Gallery image'"
                class="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                @load="imageLoading = false"
                @error="imageLoading = false"
              />
              
              <!-- Индикатор загрузки для полного изображения -->
              <div v-if="imageLoading" class="absolute inset-0 flex items-center justify-center">
                <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
              </div>
            </div>
  
            <!-- Информация об изображении -->
            <div 
              v-if="photo.title || photo.description"
              class="absolute bottom-4 left-4 right-4 bg-black bg-opacity-60 text-white p-4 rounded-lg backdrop-blur-sm"
            >
              <h3 v-if="photo.title" class="text-lg font-semibold mb-1">{{ photo.title }}</h3>
              <p v-if="photo.description" class="text-sm text-gray-200">{{ photo.description }}</p>
            </div>
          </div>
        </div>
      </Teleport>
    </div>
  </template>
  
  <script>
  import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue'

  export default {
    name: 'PhotoTile',
    components: {},
    props: {
      photo: {
        type: Object,
        required: true,
        validator: (photo) => {
          return photo && typeof photo.url === 'string'
        }
      }
    },
    emits: ['open', 'close'],
    setup(props, { emit }) {
      // Reactive state
      const isModalOpen = ref(false)
      const imageLoading = ref(false)
      const modalRef = ref(null)

      // Methods
      const openModal = () => {
        isModalOpen.value = true
        imageLoading.value = true
        document.body.style.overflow = 'hidden'
        
        nextTick(() => {
          if (modalRef.value) {
            modalRef.value.focus()
          }
        })
      }

      const closeModal = () => {
        isModalOpen.value = false
        imageLoading.value = false
        document.body.style.overflow = ''
      }

      // Keyboard event handler
      const handleKeydown = (event) => {
        if (event.key === 'Escape' && isModalOpen.value) {
          closeModal()
        }
      }

      // Lifecycle hooks
      onMounted(() => {
        document.addEventListener('keydown', handleKeydown)
      })

      onUnmounted(() => {
        document.removeEventListener('keydown', handleKeydown)
        document.body.style.overflow = ''
      })

      // Watch for modal state changes
      watch(isModalOpen, (newValue) => {
        if (newValue) {
          emit('open', props.photo)
        } else {
          emit('close', props.photo)
        }
      })

      return {
        isModalOpen,
        imageLoading,
        modalRef,
        openModal,
        closeModal
      }
    }
  }
  </script>
  
  <style scoped>
  .modal-overlay {
    animation: fadeIn 0.3s ease-out;
  }
  
  .modal-content {
    animation: scaleIn 0.3s ease-out;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  .photo-tile {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .photo-tile:hover {
    transform: translateY(-4px) scale(1.02);
  }
  
  /* Улучшенная анимация для мобильных устройств */
  @media (max-width: 768px) {
    .photo-tile:hover {
      transform: translateY(-2px) scale(1.01);
    }
    
    .modal-content {
      padding: 1rem;
    }
  }
  
  /* Accessibility improvements */
  .photo-tile:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
  
  button:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
  </style>